import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { AuthLayout } from './AuthLayout';
import { AuthError, AuthField, AuthHeading, AuthPrimaryButton, authLinkClass } from './AuthControls';
import { useForm } from 'hooks/useForm';
import { useAuth } from 'hooks/useAuth';
import { trackUmami } from '@/lib/umami/umami';

type StateType = {
  email: string;
};

export const ForgotPasswordScreen = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { forgotPassword } = useAuth();
  const { state, formRef, reset } = useForm<StateType>();
  const { email } = state;

  const isDisabled = isSubmitting || !email;

  useEffect(() => trackUmami('password-reset-viewed'), []);

  const onSubmit = async () => {
    if (isDisabled) return;
    setError(null);
    setIsSubmitting(true);
    try {
      await forgotPassword({ email });
      trackUmami('password-reset-requested');
      // Success navigates away from this screen, so it is the one message here
      // that cannot be inline.
      toast.success('Check your email for instructions on setting a new password.');
      reset();
      navigate('/auth/signin');
    } catch (ex) {
      trackUmami('password-reset-request-failed');
      const { message } = ex as { message?: string };
      setError(message || 'Could not send the recovery email. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthLayout
      footer={
        <span>
          Remembered it?{' '}
          <Link to="/auth/signin" className={`${authLinkClass} font-medium`}>
            Sign in
          </Link>
        </span>
      }
    >
      <AuthHeading title="Reset your password" subtitle="We will email you a link to set a new one." />

      <form onSubmit={onSubmit} ref={formRef} className="flex flex-col gap-[18px]">
        {!!error && <AuthError>{error}</AuthError>}

        <AuthField label="Work email" type="email" name="email" autoComplete="username" placeholder="you@company.com" />

        <AuthPrimaryButton disabled={isDisabled} isPending={isSubmitting}>
          {isSubmitting ? 'Sending…' : 'Send recovery email'}
        </AuthPrimaryButton>
      </form>
    </AuthLayout>
  );
};
